import * as yup from 'yup';
import { autocompleteSchema } from '@gov-design-system-ce/react-form';
import type { Optional } from '@gov-nx/core/types';
import type { FormAutocompleteProps } from '@gov-nx/ui/types';
import type { RequiredWhenFieldCondition } from './types/fieldCondition.types';
import { requiredWhenAll } from './utils/requiredWhenAll';

export type AutocompleteFormData<Selected> = Optional<{
	selected?: Selected;
	input?: string;
}>;

interface AutocompleteShape {
	requiredMessage?: string;
	isRequiredWhen?: {
		fieldName: string;
		is: boolean | string;
		errorMessage: string;
	};
	isRequiredWhenAll?: {
		conditions: RequiredWhenFieldCondition[];
		errorMessage: string;
	};
}

const requiredShape = (requiredMessage?: string) =>
	autocompleteSchema(
		yup.object().default(undefined).required(requiredMessage)
	).required(requiredMessage);

const getAutocompleteShape = (props?: AutocompleteShape) => {
	const schema = yup.object({
		input: yup.string(),
		selected: yup.object(),
	});

	if (props?.requiredMessage) {
		return requiredShape(props.requiredMessage);
	}

	if (props?.isRequiredWhenAll) {
		const { conditions, errorMessage } = props.isRequiredWhenAll;
		return requiredWhenAll(schema, conditions, (allMatch, objectSchema) =>
			allMatch ? requiredShape(errorMessage) : objectSchema
		);
	}

	if (props?.isRequiredWhen) {
		return schema.when(
			props.isRequiredWhen.fieldName,
			([field], objectSchema) => {
				if (field === props.isRequiredWhen?.is) {
					return requiredShape(props.isRequiredWhen?.errorMessage);
				}
				return objectSchema;
			}
		);
	}

	return schema;
};

export type AutocompleteDefinition = {
	schema: ReturnType<typeof getAutocompleteShape>;
	defaultValue: undefined | { input: string };
	field: FormAutocompleteProps;
};

interface Props {
	name: string;
	label: string;
	placeholder?: string;
	isDisabled?: boolean;
	requiredMessage?: string;
	isRequiredWhen?: {
		fieldName: string;
		is: boolean | string;
		errorMessage: string;
	};
	isRequiredWhenAll?: {
		conditions: RequiredWhenFieldCondition[];
		errorMessage: string;
	};
	defaultValue?: AutocompleteDefinition['defaultValue'];
	message?: string;
}

export const useAutocompleteDefinition = (
	props: Props
): AutocompleteDefinition => {
	const schema = getAutocompleteShape(props);

	return {
		schema,
		defaultValue: props.defaultValue ?? { input: '' },
		field: {
			field: {
				name: props.name,
				placeholder: props.placeholder,
				isDisabled: props.isDisabled,
			},
			label: {
				children: props.label,
			},
			messages: {
				messages: {
					children: props.message ? props.message : '',
				},
			},
		},
	};
};
